import React from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';
import { BORDER_GRAY } from '../constants/colors';
import KPIFilterDropdown, { KPIFilterDropdownItem } from '../../AnalyticsHub/components/KPIFilterDropdown';
import LhDatePicker from '../../../components/LhDatePicker';
import { TicketingAnalyticsFilterOptionsResponse } from './types';
import {
  TicketingAnalyticsCustomDateRange,
  TicketingAnalyticsFilterKey,
  TicketingAnalyticsSelectedFilters,
} from './types';
import { TimePeriod, TimePeriodTab } from './types';

interface TicketingAnalyticsFiltersPanelProps {
  timePeriodTabs: TimePeriodTab[];
  timePeriod: TimePeriod;
  onTimePeriodChange: (period: TimePeriod) => void;
  customDateRange: TicketingAnalyticsCustomDateRange;
  onCustomDateRangeChange: (range: TicketingAnalyticsCustomDateRange) => void;
  options: TicketingAnalyticsFilterOptionsResponse;
  selected: TicketingAnalyticsSelectedFilters;
  openFilterKey: string | null;
  onOpenFilterKeyChange: (key: string | null) => void;
  onSelectFilter: (key: TicketingAnalyticsFilterKey, value: KPIFilterDropdownItem[]) => void;
  onReset: () => void;
  onApply: () => void;
  hasChanges: boolean;
  /**
   * Optional applied-context summary rendered at the bottom of the card,
   * below a divider. Omit to render nothing.
   */
  summary?: React.ReactNode;
}

const TicketingAnalyticsFiltersPanel: React.FC<TicketingAnalyticsFiltersPanelProps> = ({
  timePeriodTabs,
  timePeriod,
  onTimePeriodChange,
  customDateRange,
  onCustomDateRangeChange,
  options,
  selected,
  openFilterKey,
  onOpenFilterKeyChange,
  onSelectFilter,
  onReset,
  onApply,
  hasChanges,
  summary,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const parseDate = (value: string | null): Date | null => {
    if (!value) {
      return null;
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed;
  };

  const formatDate = (date: Date | null): string | null => {
    if (!date) {
      return null;
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Today at midnight — no future dates should be selectable
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = parseDate(customDateRange.startDate);
  const endDate = parseDate(customDateRange.endDate);

  // Start date upper bound: whichever is earlier — end date or today
  const startMaxDate: Date = endDate && endDate < today ? endDate : today;

  const handleStartDateChange = (date: Date | null) => {
    const nextStart = formatDate(date);
    const nextRange: TicketingAnalyticsCustomDateRange = {
      startDate: nextStart,
      endDate: customDateRange.endDate,
    };

    if (
      nextRange.startDate
      && nextRange.endDate
      && new Date(`${nextRange.endDate}T00:00:00`) < new Date(`${nextRange.startDate}T00:00:00`)
    ) {
      nextRange.endDate = nextRange.startDate;
    }

    onCustomDateRangeChange(nextRange);
  };

  const handleEndDateChange = (date: Date | null) => {
    const nextEnd = formatDate(date);
    const nextRange: TicketingAnalyticsCustomDateRange = {
      startDate: customDateRange.startDate,
      endDate: nextEnd,
    };

    if (
      nextRange.startDate
      && nextRange.endDate
      && new Date(`${nextRange.endDate}T00:00:00`) < new Date(`${nextRange.startDate}T00:00:00`)
    ) {
      nextRange.startDate = nextRange.endDate;
    }

    onCustomDateRangeChange(nextRange);
  };

  return (
    <View style={[styles.filtersContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
      <View style={styles.timePeriodRow}>
        <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>TIME PERIOD</Text>
        <View style={styles.timePeriodRowInline}>
          <View style={[styles.timePeriodTabs, isDark && { borderColor: colors.border.default }]}>
          {timePeriodTabs.map((tp) => (
            <TouchableOpacity
              key={tp.key}
              style={[
                styles.timePeriodTab,
                isDark && { backgroundColor: colors.background.elevated },
                timePeriod === tp.key && styles.timePeriodTabActive,
                timePeriod === tp.key && isDark && { backgroundColor: colors.brand.primary },
              ]}
              onPress={() => onTimePeriodChange(tp.key)}
              accessibilityRole="button"
            >
              <Text style={[
                styles.timePeriodTabLabel,
                isDark && { color: colors.text.secondary },
                timePeriod === tp.key && styles.timePeriodTabLabelActive,
              ]}>
                {tp.label}
              </Text>
            </TouchableOpacity>
          ))}
          </View>

          {timePeriod === 'custom' ? (
            <View style={styles.customDateRangeWrap}>
              <View style={styles.customDatePickerItem}>
                <LhDatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  placeholder="Start date"
                  maximumDate={startMaxDate}
                  colorScheme={isDark ? "dark" : "light"}
                  iconColor={isDark ? "#FFFFFF" : "#555555"}
                />
              </View>
              <Text style={[styles.customDateRangeTo, isDark && { color: colors.text.secondary }]}>to</Text>
              <View style={styles.customDatePickerItem}>
                <LhDatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  placeholder="End date"
                  minimumDate={startDate || undefined}
                  maximumDate={today}
                  colorScheme={isDark ? "dark" : "light"}
                  iconColor={isDark ? "#FFFFFF" : "#555555"}
                />
              </View>
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.filtersRow}>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>REGION</Text>
          <KPIFilterDropdown
            childList={options.regions}
            selectedValue={selected.regions}
            setSelectedValue={(value) => onSelectFilter('regions', value)}
            placeholder="All"
            isOpen={openFilterKey === 'region'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'region' : null)}
            testID="ta-filter-region"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>MARKET</Text>
          <KPIFilterDropdown
            childList={options.markets}
            selectedValue={selected.markets}
            setSelectedValue={(value) => onSelectFilter('markets', value)}
            placeholder="All"
            isOpen={openFilterKey === 'market'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'market' : null)}
            testID="ta-filter-market"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>LOCATION</Text>
          <KPIFilterDropdown
            childList={options.locations}
            selectedValue={selected.locations}
            setSelectedValue={(value) => onSelectFilter('locations', value)}
            placeholder="All"
            isOpen={openFilterKey === 'location'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'location' : null)}
            testID="ta-filter-location"
            autoFocusSearchOnOpen
            sortItems
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>CATEGORY</Text>
          <KPIFilterDropdown
            childList={options.categories}
            selectedValue={selected.categories}
            setSelectedValue={(value) => onSelectFilter('categories', value)}
            placeholder="All"
            isOpen={openFilterKey === 'category'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'category' : null)}
            testID="ta-filter-category"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>SUB-CATEGORY</Text>
          <KPIFilterDropdown
            childList={options.subCategories}
            selectedValue={selected.subCategories}
            setSelectedValue={(value) => onSelectFilter('subCategories', value)}
            placeholder="All"
            isOpen={openFilterKey === 'subcategory'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'subcategory' : null)}
            testID="ta-filter-subcategory"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={styles.filterLabel}>TERTIARY</Text>
          <KPIFilterDropdown
            childList={options.tertiaries}
            selectedValue={selected.tertiaries}
            setSelectedValue={(value) => onSelectFilter('tertiaries', value)}
            placeholder="All"
            isOpen={openFilterKey === 'tertiary'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'tertiary' : null)}
            testID="ta-filter-tertiary"
            autoFocusSearchOnOpen
          />
        </View>
      </View>

      <View style={styles.filtersRow}>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>STATUS</Text>
          <KPIFilterDropdown
            childList={options.statuses}
            selectedValue={selected.statuses}
            setSelectedValue={(value) => onSelectFilter('statuses', value)}
            placeholder="All"
            isOpen={openFilterKey === 'status'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'status' : null)}
            testID="ta-filter-status"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>RESOLUTION PATH</Text>
          <KPIFilterDropdown
            childList={options.resolutionPaths}
            selectedValue={selected.resolutionPaths}
            setSelectedValue={(value) => onSelectFilter('resolutionPaths', value)}
            placeholder="All"
            isOpen={openFilterKey === 'resolution'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'resolution' : null)}
            testID="ta-filter-resolution"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>URGENCY</Text>
          <KPIFilterDropdown
            childList={options.urgencies}
            selectedValue={selected.urgencies}
            setSelectedValue={(value) => onSelectFilter('urgencies', value)}
            placeholder="All"
            isOpen={openFilterKey === 'urgency'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'urgency' : null)}
            testID="ta-filter-urgency"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterItem}>
          <Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>ASSIGNED</Text>
          <KPIFilterDropdown
            childList={options.assignedTo}
            selectedValue={selected.assignedTo}
            setSelectedValue={(value) => onSelectFilter('assignedTo', value)}
            placeholder="All"
            isOpen={openFilterKey === 'assigned'}
            onOpenChange={(open) => onOpenFilterKeyChange(open ? 'assigned' : null)}
            testID="ta-filter-assigned"
            autoFocusSearchOnOpen
          />
        </View>
        <View style={styles.filterActionsInline}>
          <TouchableOpacity
            style={[styles.resetBtn, isDark && { borderColor: colors.border.default }]}
            onPress={onReset}
            accessibilityRole="button"
            accessibilityLabel="Reset filters"
          >
            <Text style={[styles.resetBtnText, isDark && { color: colors.text.primary }]}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.applyBtn, !hasChanges && styles.applyBtnDisabled, isDark && !hasChanges && { backgroundColor: colors.background.elevated }]}
            onPress={onApply}
            accessibilityRole="button"
            accessibilityLabel="Apply filters"
            disabled={!hasChanges}
          >
            <Text style={[styles.applyBtnText, !hasChanges && styles.applyBtnTextDisabled]}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>

      {summary ? <View style={[styles.summarySection, isDark && { borderTopColor: colors.border.default }]}>{summary}</View> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderRadius: 8,
    padding: 16,
    marginTop: 8,
    marginBottom: 8,
    gap: 12,
  },
  timePeriodRow: {
    gap: 8,
  },
  timePeriodRowInline: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
  },
  timePeriodTabs: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 8,
    overflow: 'hidden',
    alignSelf: 'flex-start',
  },
  timePeriodTab: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
  },
  timePeriodTabActive: {
    backgroundColor: '#005CBC',
  },
  timePeriodTabLabel: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#344054',
  },
  timePeriodTabLabelActive: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  customDateRangeWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  customDatePickerItem: {
    minWidth: 170,
    maxWidth: 220,
  },
  customDateRangeTo: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#667085',
  },
  filtersRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
  },
  filterItem: {
    minWidth: 140,
    maxWidth: 200,
    flexGrow: 1,
  },
  filterLabel: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  filterActionsInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginLeft: 'auto',
    marginTop: 20,
  },
  resetBtn: {
    borderWidth: 1,
    borderColor: '#D0D5DD',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    minHeight: 40,
    justifyContent: 'center',
  },
  resetBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#344054',
  },
  applyBtn: {
    backgroundColor: '#0B5FC6',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    minHeight: 40,
    justifyContent: 'center',
  },
  applyBtnDisabled: {
    backgroundColor: '#D0D5DD',
  },
  applyBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
  },
  applyBtnTextDisabled: {
    color: '#9AA1AB',
  },
  summarySection: {
    borderTopWidth: 1,
    borderTopColor: BORDER_GRAY,
    paddingTop: 12,
  },
});

export default TicketingAnalyticsFiltersPanel;






